import { useEffect, useId, useRef, useState } from "react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

export interface TypedConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmationText: string;
  inputLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  submittingLabel?: string;
  errorLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
}

/**
 * A confirmation dialog for irreversible operations. Requiring an explicit
 * phrase keeps the primary action difficult to trigger by accident while the
 * surrounding interaction remains short and predictable.
 */
export function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmationText,
  inputLabel = `Type ${confirmationText} to continue`,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  submittingLabel = "Working…",
  errorLabel = "The operation failed.",
  destructive = false,
  onConfirm,
}: TypedConfirmDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const confirmed = value.trim() === confirmationText;

  useEffect(() => {
    if (!open) {
      setValue("");
      setSubmitting(false);
      setError(undefined);
    }
  }, [open]);

  const confirm = async () => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    setError(undefined);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : errorLabel);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!submitting) onOpenChange(nextOpen);
      }}
      title={title}
      description={description}
      initialFocusRef={inputRef}
      footer={
        <div className="ui-confirm-dialog__actions">
          <Button
            variant="ghost"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            disabled={!confirmed || submitting}
            onClick={() => void confirm()}
          >
            {submitting ? submittingLabel : confirmLabel}
          </Button>
        </div>
      }
    >
      <form
        className="ui-typed-confirm-dialog__form"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <label htmlFor={inputId}>{inputLabel}</label>
        <input
          ref={inputRef}
          id={inputId}
          value={value}
          autoComplete="off"
          spellCheck={false}
          disabled={submitting}
          onChange={(event) => setValue(event.currentTarget.value)}
        />
        {error ? (
          <p className="ui-confirm-dialog__error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
